import React, { useEffect, useMemo, useRef, useState } from 'react';
import './ChatMessagesHistory.scss';
import classNames from 'classnames';
import {
  addMessageToChatHistory,
  useApplyContent,
  useCreateMessage,
  useFetchChatDetail,
  useFetchChatHistory,
  useUpdateChat,
} from 'hooks/ai-chat';
import { fetchChatDetail } from 'apis/AiChat/AiChat';
import {
  AIMessage,
  AIMessageActionType,
  AIMessageContentType,
  AIMessageType,
  AiChatHistory,
  AiChatType,
  ChatDetailResponse,
  CreateMessagePayload,
  ErrorResponse,
} from 'types';
import { ChatInput } from '../ChatInput';
import UserUploadPrompt from '../UserUploadPrompt/UserUploadPrompt';
import { AppBadge } from 'components/AppBadge';
import { ChevronRight } from 'react-feather';
import { useQueryClient } from 'react-query';
import ChatMessages from '../ChatMessages/ChatMessages';
import AIMascot from '../AIMascot/AIMascot';
import happyFace from 'assets/images/ai-assistant/face-happy.svg';
import { getBrainstormTopicSuggestions } from 'components/AIOffset';
import { Loading } from 'components/Loading';
import BottomActions from '../BottomActions/BottomActions';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppStore } from 'hooks/useAppStore';
import selectContent from 'assets/images/ai-assistant/message-actions/select-content.svg';
import applyCreate from 'assets/images/ai-assistant/message-actions/apply-create.svg';
import RefineModal from '../RefineModal/RefineModal';
import SelectContentModal from '../SelectContentModal/SelectContentModal';
import ActionButtons from '../ActionButtons/ActionButtons';
import { HelpTipsProvider } from 'components/AIOffset/components/HelpTips/HelpTipsProvider';
import HelpTips from 'components/AIOffset/components/HelpTips/HelpTips';
import {
  REFINE_CONTENT_TIP,
  SELECT_OTHER_TIP,
  APPLY_CREATE_TIP,
  SELECT_CONTENT_TIP,
  REFINE_CONTEXT_TIP,
} from 'constants/help-tip';
import { useRegenerateMessage } from 'hooks/ai-chat/useRegenerateMessage';
import { ChatModalRetry } from '../ChatModalRetry';
import { observer } from 'mobx-react-lite';

interface Props {
  chatId: string;
  previousViewMode?: AiChatType;
}

export const ChatMessagesHistory = observer(({ chatId, previousViewMode }: Props) => {
  const { data: chatDetail, isLoading: isGetChatDetailLoading } = useFetchChatDetail(chatId);
  const messages = useMemo(() => {
    return chatDetail?.messages || [];
  }, [chatDetail?.messages]);

  const importedDocuments = useMemo(() => {
    return chatDetail?.aiChat?.importedDocuments || {};
  }, [chatDetail?.aiChat?.importedDocuments]);

  const queryClient = useQueryClient();
  const { data: chatHistory = [] } = useFetchChatHistory();
  const {
    mutateAsync: createNewMessage,
    isLoading: isCreateMessageLoading,
    isError: isErrorCreateMessage,
    error: errorCreateMessage,
    variables: variablesCreateMessage,
  } = useCreateMessage();
  const {
    mutateAsync: analyzeContent,
    isLoading: isAnalyzing,
    isError: isErrorAnalyzeContent,
    error: errorAnalyzeContent,
    variables: variableAnalyzeContent,
  } = useCreateMessage();
  const {
    mutateAsync: refine,
    isLoading: isRefineLoading,
    isError: isErrorRefine,
    error: errorRefine,
    variables: variablesRefine,
  } = useCreateMessage();
  const {
    mutateAsync: generateContentMessage,
    isLoading: isGenerateLoading,
    isError: isErrorGenerateContentMessage,
    error: errorGenerateContentMessage,
    variables: variablesGenerateContentMessage,
  } = useCreateMessage();

  const [errorUseCreateMessage, setErrorUseCreateMessage] = useState(null);

  const { mutateAsync: regenerateMessage, isLoading: isRegenerateLoading } = useRegenerateMessage();
  const { mutate: updateChat } = useUpdateChat({});
  const bottomElementRef = useRef<HTMLDivElement>();
  const [showChatInput, setShowChatInput] = useState(true);

  const [isRefineModalOpen, setIsRefineModalOpen] = useState(false);
  const [isSelectContentTypeOpen, setIsSelectContentTypeOpen] = useState(false);
  const [currentActionType, setCurrentActionType] = useState<AIMessageActionType | null>(null);
  const [currentMessageContentType, setCurrentMessageContentType] = useState<AIMessageContentType | null>(null);
  const { mutateAsync: applyContent, isLoading: isApplyLoading } = useApplyContent();
  const navigate = useNavigate();
  const location = useLocation();
  const { state } = location;
  const {
    ltiStore: { workspaceId },
    ltiMode,
    uiStore,
  } = useAppStore();
  const lastMessage = messages[messages.length - 1];
  const messageContentType = lastMessage?.messageContentType;
  const renderHelpTips = !isRefineModalOpen && !isSelectContentTypeOpen && !isRegenerateLoading;

  const getHelpTips = () => {
    if (messageContentType === AIMessageContentType.Content) {
      return [APPLY_CREATE_TIP, REFINE_CONTENT_TIP, SELECT_OTHER_TIP];
    }
    if (messageContentType === AIMessageContentType.Context) {
      return [SELECT_CONTENT_TIP, REFINE_CONTEXT_TIP];
    }
    return [];
  };

  const helpTips = getHelpTips();

  const showFinishBrainstormAnalyzeContentButton = () => {
    return messages.some(message => message.content.includes('Finish Brainstorming'));
  };

  const brainstormChats = useMemo<AiChatHistory[]>(() => {
    return chatHistory.filter(chat => chat.chatType === AiChatType.BRAINSTORM);
  }, [chatHistory]);

  const defaultChats = useMemo<Partial<AiChatHistory>[]>(() => {
    return brainstormChats.length ? brainstormChats : getBrainstormTopicSuggestions();
  }, [chatHistory]);

  const handleAnalyzeContent = async ({ chatId, content, actionType }: Partial<CreateMessagePayload>) => {
    const dataMessage = await analyzeContent({
      chatId,
      content,
      actionType,
    });

    queryClient.setQueryData<ChatDetailResponse>(['chat-detail', chatId], chatDetail => {
      return {
        ...chatDetail,
        aiChat: {
          ...chatDetail.aiChat,
          aiContext: dataMessage.content,
        },
      };
    });

    // Fetch new name generated from ai if user didn't update the name
    if (chatDetail?.aiChat?.userUpdatedName === false) {
      await updateChatNameHistory(chatId);
    }
  };

  const onAnalyzeBrainstormingContent = async () => {
    setShowChatInput(false);
    const analyzeMessage = 'Finish Brainstorming & Analyse Content';

    addMessageToChatHistory({
      queryClient,
      chatId,
      message: {
        messageType: AIMessageType.Human,
        content: analyzeMessage,
        messageContentType: AIMessageContentType.Text,
        actionType: AIMessageActionType.Chat,
      },
    });

    setTimeout(scrollToBottom, 100);

    await handleAnalyzeContent({
      chatId,
      content: analyzeMessage,
      actionType: AIMessageActionType.GenerateContext,
    });
  };

  const updateChatNameHistory = async (chatId: string) => {
    const updatedChatDetail = await queryClient.fetchQuery<ChatDetailResponse>(['chat-detail', chatId], () =>
      fetchChatDetail(chatId, { limit: 50, offset: 0 }),
    );

    const newChatName = updatedChatDetail?.aiChat?.name;

    if (newChatName && newChatName !== chatDetail?.aiChat?.name) {
      queryClient.setQueryData<AiChatHistory[]>('chat-history', (chatsList: AiChatHistory[] = []) => {
        const existingChatIndex = chatsList.findIndex(chat => chat.id === chatId);
        if (existingChatIndex >= 0) {
          // Update existing chat in the list
          const updatedChats = [...chatsList];
          updatedChats[existingChatIndex] = { ...updatedChats[existingChatIndex], name: newChatName };
          return updatedChats;
        } else {
          // Add new chat to the beginning of the list
          return [{ ...chatDetail.aiChat, name: newChatName }, ...chatsList];
        }
      });
    }
  };

  const handleCreateNewMessage = async (payload: CreateMessagePayload) => {
    const { chatId, content, actionType } = payload;
    const newInputMessage = await createNewMessage({
      chatId,
      content,
      actionType,
    });

    // Update Chat aiContext if user finish brainstorming by chat input
    if (actionType === AIMessageActionType.Chat && newInputMessage.actionType === AIMessageActionType.GenerateContext) {
      // First update the ai context
      queryClient.setQueryData<ChatDetailResponse>(['chat-detail', chatId], chatDetail => ({
        ...chatDetail,
        aiChat: {
          ...chatDetail.aiChat,
          aiContext: newInputMessage.content,
        },
      }));

      // Update chat name history
      await updateChatNameHistory(chatId);
    }

    const isChatNameUnset = chatDetail?.aiChat?.name === null;

    if (isChatNameUnset) {
      // Prioritize the user input over the ai generated content
      updateChatName((content || newInputMessage.content).split('.')[0]);
    }
  };

  const handleCreateDetailedContext = async () => {
    addMessageToChatHistory({
      queryClient,
      chatId,
      message: {
        content: 'Create detailed context',
        actionType: AIMessageActionType.Chat,
        messageContentType: AIMessageContentType.Text,
        messageType: AIMessageType.Human,
      },
    });

    setTimeout(scrollToBottom, 100);

    await handleCreateNewMessage({ chatId, content: 'Create detailed context', actionType: AIMessageActionType.Chat });
  };

  const onSubmitInput = async ({
    chatId,
    content,
    actionType,
  }: {
    chatId: string;
    content: string;
    actionType: AIMessageActionType;
  }) => {
    await handleCreateNewMessage({ chatId, content, actionType });
  };

  const updateChatName = (name: string) => {
    updateChat({ chatId, name });

    queryClient.setQueryData<AiChatHistory[]>('chat-history', (chatsList: AiChatHistory[] = []) => {
      return [{ ...chatDetail.aiChat, name }, ...chatsList];
    });

    queryClient.setQueryData<ChatDetailResponse>(['chat-detail', chatId], chatDetail => ({
      ...chatDetail,
      aiChat: {
        ...chatDetail.aiChat,
        name,
      },
    }));
  };

  const isChatAnalyzed = useMemo(() => {
    return !!chatDetail?.aiChat?.aiContext;
  }, [chatDetail]);

  const welcomeMessage = useMemo(() => {
    return chatDetail?.aiChat?.chatType === AiChatType.IMPORT ? (
      <div className="d-flex justify-content-end">
        <UserUploadPrompt
          chatId={chatId}
          importedDocuments={importedDocuments}
        />
      </div>
    ) : (
      !messages.length && (
        <div className="gap-2 d-flex flex-column">
          <div className="d-flex align-items-center">
            <AIMascot
              className="ai-mascot ai-mascot-small"
              faceSrc={happyFace}
              isFloat={false}
            />
            <div className="font-size-12 fw-semibold text-primary-400 ms-2">Michi Assistant</div>
          </div>
          <div className="guide bg-neutral-50 font-size-14 fw-normal text-neutral-900">
            Hello! What subject area or topic are you interested in creating educational content for today?
          </div>
        </div>
      )
    );
  }, [chatDetail?.aiChat?.chatType, messages?.length]);

  const scrollToBottom = () => {
    if (!bottomElementRef.current) return;

    const scrollContainer = bottomElementRef.current.closest('.overflow-auto');
    if (scrollContainer) {
      scrollContainer.scrollTo({
        top: scrollContainer.scrollHeight,
      });
    }
  };

  const handleAction = async (message: AIMessage) => {
    if ([AIMessageActionType.GenerateContent, AIMessageActionType.RefineContent].includes(message.actionType)) {
      const data = await applyContent({ chatId, messageId: message.id });
      const library = data.metadata.structuredContent.library;
      const messageId = data.id;
      addNew(library, messageId);
      setTimeout(() => {
        uiStore.toggleAIOffset();
        uiStore.setAIScrollToBottom(true);
      }, 500);
    } else {
      handleOpenSelectContentModal();
    }
  };

  const handleRefineClick = (actionType: AIMessageActionType, messageContentType: AIMessageContentType) => {
    setCurrentActionType(actionType);
    setCurrentMessageContentType(messageContentType);
    setIsRefineModalOpen(true);
    setIsSelectContentTypeOpen(false);
    setTimeout(scrollToBottom, 100);
    uiStore.setAIScrollToBottom(false);
  };

  const handleOpenSelectContentModal = () => {
    setIsSelectContentTypeOpen(true);
    setIsRefineModalOpen(false);
    setTimeout(scrollToBottom, 100);
    uiStore.setAIScrollToBottom(false);
  };

  const handleCloseSelectContentModal = () => {
    setIsSelectContentTypeOpen(false);
  };

  const handleCloseRefineModal = () => {
    setIsRefineModalOpen(false);
    setCurrentActionType(null);
    setCurrentMessageContentType(null);
  };

  const addNew = (library: string, messageId: string) => {
    const navigateFrom = state?.from ?? location.pathname;
    const shouldRefresh = true;
    const url = ltiMode ? `/${workspaceId}/lti/launch` : '';

    navigate(`${url}/contents/new?library=${library}&aiMessageId=${messageId}`, {
      replace: true,
      state: {
        shouldRefresh,
        from: navigateFrom,
      },
    });
  };

  const actionLabel = (actionType: AIMessageActionType) => {
    if (actionType === AIMessageActionType.GenerateContent || actionType === AIMessageActionType.RefineContent)
      return 'Apply & Create';

    return 'Select Content';
  };

  const renderIcon = (actionType: AIMessageActionType) => {
    if (actionType === AIMessageActionType.GenerateContent || actionType === AIMessageActionType.RefineContent)
      return applyCreate;

    return selectContent;
  };

  const getRefineLabel = (actionType: AIMessageActionType | null) => {
    if (actionType === AIMessageActionType.GenerateContent || actionType === AIMessageActionType.RefineContent) {
      return 'Refine Content';
    }
    return 'Refine Context';
  };

  const renderModal = () => {
    if (isRefineModalOpen) {
      return (
        <RefineModal
          label={getRefineLabel(currentActionType)}
          onClose={handleCloseRefineModal}
          currentMessageContentType={currentMessageContentType}
          chatId={chatId}
          onSubmit={refine}
        />
      );
    }

    if (isSelectContentTypeOpen) {
      return (
        <SelectContentModal
          onClose={handleCloseSelectContentModal}
          chatId={chatId}
          generateContentMessage={generateContentMessage}
        />
      );
    }

    return renderActionButtons();
  };

  const renderActionButtons = () => {
    if (messages.length > 0) {
      const lastMessage = messages[messages.length - 1];
      return (
        <ActionButtons
          lastMessage={lastMessage}
          isApplyLoading={isApplyLoading}
          isRefineLoading={isRefineLoading}
          isGenerateLoading={isGenerateLoading}
          isAnalyzing={isAnalyzing}
          handleAction={handleAction}
          handleRefineClick={handleRefineClick}
          handleOpenSelectContentModal={handleOpenSelectContentModal}
          renderIcon={renderIcon}
          actionLabel={actionLabel}
          getRefineLabel={getRefineLabel}
        />
      );
    }
    return null;
  };

  const handleErrorMessages = (error: ErrorResponse) => {
    if (error && typeof error.message === 'string' && error.message.includes('AI Warning:')) {
      return error.message.replace('AI Warning:', '');
    } else {
      return 'Oops! We encountered an issue processing your input. Please review and try again.';
    }
  };

  const handleRetry = async () => {
    switch (errorUseCreateMessage?.isError) {
      case isErrorCreateMessage:
        await handleCreateNewMessage(errorUseCreateMessage.variables);
        break;
      case isErrorAnalyzeContent:
        await handleAnalyzeContent(errorUseCreateMessage.variables);
        break;
      case isErrorRefine:
        await refine(errorUseCreateMessage.variables);
        break;
      case isErrorGenerateContentMessage:
        await generateContentMessage(errorUseCreateMessage.variables);
        break;
      default:
        console.error('Unknown error type');
        break;
    }
  };

  const handleOnBack = () => {
    setErrorUseCreateMessage(null);

    // Remove the last message from the chat history
    queryClient.setQueryData<ChatDetailResponse>(['chat-detail', chatId], chatDetail => {
      return {
        ...chatDetail,
        messages: chatDetail.messages.slice(0, -1),
      };
    });

    if (isErrorRefine) {
      setIsRefineModalOpen(true);
      return;
    }

    if (isErrorGenerateContentMessage) {
      setIsSelectContentTypeOpen(true);
      return;
    }

    if (isErrorCreateMessage || isErrorAnalyzeContent) {
      setShowChatInput(true);
      return;
    }
  };

  useEffect(() => {
    if (isCreateMessageLoading) {
      setTimeout(scrollToBottom, 100);
    }
  }, [isCreateMessageLoading]);

  const onSelectDefaultMessage = async (content: string) => {
    addMessageToChatHistory({
      queryClient,
      chatId,
      message: {
        content,
        actionType: AIMessageActionType.Chat,
        messageContentType: AIMessageContentType.Text,
        messageType: AIMessageType.Human,
      },
    });

    await onSubmitInput({ chatId, content, actionType: AIMessageActionType.Chat });
  };

  useEffect(() => {
    if (isErrorCreateMessage) {
      setErrorUseCreateMessage({
        error: errorCreateMessage,
        isError: isErrorCreateMessage,
        variables: variablesCreateMessage,
      });
    } else if (isErrorAnalyzeContent) {
      setErrorUseCreateMessage({
        error: errorAnalyzeContent,
        isError: isErrorAnalyzeContent,
        variables: variableAnalyzeContent,
      });
    } else if (isErrorRefine) {
      setErrorUseCreateMessage({
        error: errorRefine,
        isError: isErrorRefine,
        variables: variablesRefine,
      });
    } else if (isErrorGenerateContentMessage) {
      setErrorUseCreateMessage({
        error: errorGenerateContentMessage,
        isError: isErrorGenerateContentMessage,
        variables: variablesGenerateContentMessage,
      });
    } else {
      setErrorUseCreateMessage(null);
    }
  }, [isErrorCreateMessage, isErrorAnalyzeContent, isErrorRefine, isErrorGenerateContentMessage]);

  useEffect(() => {
    if (previousViewMode === AiChatType.HISTORY) {
      uiStore.setAIScrollToBottom(true);
    }
  }, [uiStore.isAIScrollToBottom, previousViewMode]);

  if (isGetChatDetailLoading) {
    return <Loading />;
  }

  return (
    <div className="d-flex flex-column h-100">
      <div className="flex-grow-1 overflow-auto mb-3">
        <div
          className={classNames('chat-messages-history bg-white p-4 pb-0 h-100 d-flex flex-column', {
            'pb-0': chatDetail?.aiChat?.chatType === AiChatType.BRAINSTORM,
          })}
        >
          {welcomeMessage}
          <div className="bg-white gap-2 flex-fill d-flex flex-column h-100 mb-2">
            <ChatMessages
              isChatAnalyzed={!!messages.length && isChatAnalyzed}
              chatId={chatId}
              messages={messages}
              isNewMessageLoading={isCreateMessageLoading}
              isAnalyzing={isAnalyzing}
              isRefineLoading={isRefineLoading}
              isSelectContentLoading={isGenerateLoading}
              regenerateMessage={regenerateMessage}
              isRegenerateLoading={isRegenerateLoading}
              createDetailedContext={handleCreateDetailedContext}
            />
          </div>

          <div ref={bottomElementRef} />
        </div>
      </div>

      <div className="bg-white px-4">
        {errorUseCreateMessage ? (
          <ChatModalRetry
            errorMessage={handleErrorMessages(errorUseCreateMessage.error as ErrorResponse)}
            onRetry={handleRetry}
            onBack={handleOnBack}
          />
        ) : (
          <>
            {chatDetail?.aiChat?.chatType === AiChatType.BRAINSTORM && (
              <>
                {!messages.length && (
                  <div className="gap-2 mb-5 d-flex flex-column">
                    {brainstormChats.length > 0 && (
                      <div className="d-flex align-items-center justify-content-end fonts-size-14 fw-medium text-neutral-500">
                        Your previous brainstorming topics...
                      </div>
                    )}

                    {defaultChats.slice(0, 2).map((chat, index) => {
                      return (
                        <div
                          onClick={() => onSelectDefaultMessage(chat.name)}
                          key={index}
                          className="cursor-pointer d-flex align-items-center justify-content-end font-size-14 custom-badge"
                        >
                          <AppBadge
                            type="neutral-outline-hover"
                            label={
                              <div className="w-100 d-flex align-items-center">
                                <div className="previous-brainstorm-topic w-100">{chat.name}</div>

                                <ChevronRight
                                  size={16}
                                  className="text-neutral-900"
                                />
                              </div>
                            }
                          />
                        </div>
                      );
                    })}
                  </div>
                )}

                {messages.length > 1 &&
                  !isChatAnalyzed &&
                  !isAnalyzing &&
                  !isCreateMessageLoading &&
                  showFinishBrainstormAnalyzeContentButton() && (
                    <div className="gap-2 mb-5 d-flex flex-column">
                      <div
                        onClick={() => onAnalyzeBrainstormingContent()}
                        className="cursor-pointer d-flex align-items-center justify-content-end font-size-14 custom-badge"
                      >
                        <AppBadge
                          type="primary-outline-hover"
                          label={
                            <>
                              Finish Brainstorming & Analyse Content
                              <ChevronRight
                                size={16}
                                className="text-neutral-900 ms-2"
                              />
                            </>
                          }
                        />
                      </div>
                    </div>
                  )}

                {!isChatAnalyzed && showChatInput && (
                  <ChatInput
                    chat={chatDetail}
                    submitInput={onSubmitInput}
                    isLoading={isCreateMessageLoading}
                  />
                )}
              </>
            )}

            {((!!messages.length && isChatAnalyzed) || isAnalyzing) && (
              <BottomActions
                renderHelpTips={() =>
                  renderHelpTips && (
                    <div className="mb-2">
                      <HelpTipsProvider defaultOpen={false}>
                        <HelpTips
                          helpTips={helpTips}
                          key={messageContentType}
                        />
                      </HelpTipsProvider>
                    </div>
                  )
                }
                isWelcomeSection={false}
                loading={isRefineLoading || isGenerateLoading || isAnalyzing || isRegenerateLoading}
              >
                {renderModal()}
              </BottomActions>
            )}
          </>
        )}
      </div>
    </div>
  );
});
